export interface StatusOverview {
  pendingQuotes: number;
  activeOrders: number;
  unreadMessages: number;
  completedAiPreviews: number;
  pendingPayments: number;
  inTransitShipments: number;
}

export interface ActionItem {
  id: string;
  type: 'quote_review' | 'payment_due' | 'address_needed' | 'profile_incomplete' | 'sample_approval';
  message: string;
  link: string;
  priority: 'high' | 'medium' | 'low';
  createdAt: string;
}

export interface RecentQuote {
  id: string;
  quoteNumber: string;
  productSummary: string;
  finishSummary: string;
  quantity: number;
  status: 'draft' | 'submitted' | 'quoted' | 'accepted' | 'rejected' | 'expired';
  amount: number | null;
  currency: string;
  updatedAt: string;
}

export interface AiPreview {
  id: string;
  productName: string;
  originalImage: string;
  generatedImage: string;
  finishType: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  quoteRequestId: string | null;
  createdAt: string;
}

export interface ActiveOrder {
  id: string;
  orderNumber: string;
  quoteNumber: string;
  productSummary: string;
  paymentStatus: 'pending' | 'paid' | 'partial' | 'refunded';
  productionStatus: 'not_started' | 'in_progress' | 'completed';
  shippingStatus: 'not_shipped' | 'packing' | 'shipped' | 'delivered';
  totalAmount: number;
  currency: string;
  estimatedDelivery: string | null;
}

export interface PaymentItem {
  id: string;
  paymentDate: string;
  referenceNumber: string;
  amount: number;
  currency: string;
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  methodBrand?: string;
  methodLast4?: string;
}

export interface ShipmentItem {
  id: string;
  orderNumber: string;
  carrier: string;
  trackingNumber: string | null;
  status: 'pre_transit' | 'in_transit' | 'out_for_delivery' | 'delivered' | 'exception';
  estimatedDelivery: string | null;
}

export interface MessageItem {
  id: string;
  title: string;
  relatedType: 'quote' | 'order' | 'sample' | 'general' | null;
  relatedNumber: string | null;
  unread: boolean;
  date: string;
  preview: string;
}

export interface SampleRequest {
  id: string;
  requestNumber: string;
  productName: string;
  finishSummary: string;
  quantity: number;
  status: 'requested' | 'under_review' | 'quoted' | 'in_production' | 'shipped' | 'completed' | 'cancelled';
  estimatedCost: number | null;
  currency: string;
  createdAt: string;
}

export interface UploadedFile {
  id: string;
  fileName: string;
  fileType: string;
  purpose: 'image_search' | 'quote_reference' | 'ai_finish_source' | 'sample_reference' | 'order_document';
  relatedType: 'quote' | 'order' | 'ai_preview' | 'sample' | null;
  relatedNumber: string | null;
  uploadedAt: string;
}

export interface SavedProduct {
  id: string;
  productName: string;
  category: string;
  sku: string;
  image: string;
}

export interface CompanyProfile {
  completionPercent: number;
  missingFields: string[];
  companyName: string;
}

export interface MarketSetting {
  country: string;
  language: string;
  displayCurrency: string;
  quoteBaseCurrency: string;
  exchangeRateDate: string | null;
}

export const mockStatusOverview: StatusOverview = {
  pendingQuotes: 2,
  activeOrders: 1,
  unreadMessages: 3,
  completedAiPreviews: 4,
  pendingPayments: 1,
  inTransitShipments: 1,
};

export const mockActionItems: ActionItem[] = [
  {
    id: 'a1',
    type: 'quote_review',
    message: 'Quote Q-2025-0842 is awaiting your review. Review and approve to proceed with production.',
    link: '/my/quotes',
    priority: 'high',
    createdAt: '2025-05-24T09:30:00Z',
  },
  {
    id: 'a2',
    type: 'payment_due',
    message: 'Payment of $4,250.00 is due for Order O-2025-0156. Secure your production slot.',
    link: '/my/payments',
    priority: 'high',
    createdAt: '2025-05-23T14:00:00Z',
  },
  {
    id: 'a3',
    type: 'profile_incomplete',
    message: 'Your company profile is 60% complete. Add shipping address and tax ID to speed up checkout.',
    link: '/my/company',
    priority: 'medium',
    createdAt: '2025-05-20T10:00:00Z',
  },
];

export const mockRecentQuotes: RecentQuote[] = [
  {
    id: 'q1',
    quoteNumber: 'Q-2025-0842',
    productSummary: 'Airless Bottle 50ml — Soft-touch body + UV coating cap',
    finishSummary: 'Matte soft-touch + Glossy UV cap',
    quantity: 5000,
    status: 'quoted',
    amount: 4250.00,
    currency: 'USD',
    updatedAt: '2025-05-24T09:30:00Z',
  },
  {
    id: 'q2',
    quoteNumber: 'Q-2025-0891',
    productSummary: 'Cream Jar 30ml — Double-wall acrylic',
    finishSummary: 'Frosted body + Gold hot-stamp logo',
    quantity: 3000,
    status: 'submitted',
    amount: null,
    currency: 'USD',
    updatedAt: '2025-05-22T16:45:00Z',
  },
  {
    id: 'q3',
    quoteNumber: 'Q-2025-0715',
    productSummary: 'Pump Bottle 100ml — PETG thick-wall',
    finishSummary: 'Transparent + Silk-screen 2-color',
    quantity: 10000,
    status: 'accepted',
    amount: 12800.00,
    currency: 'USD',
    updatedAt: '2025-05-18T11:20:00Z',
  },
];

export const mockAiPreviews: AiPreview[] = [
  {
    id: 'ai1',
    productName: 'Airless Bottle 50ml',
    originalImage: 'https://images.unsplash.com/photo-1620916566398-39f1143ab7be?w=300&h=300&fit=crop',
    generatedImage: 'https://images.unsplash.com/photo-1608248543803-ba4f8c70ae0b?w=300&h=300&fit=crop',
    finishType: 'Matte Soft-touch + Rose Gold Cap',
    status: 'completed',
    quoteRequestId: null,
    createdAt: '2025-05-21T13:00:00Z',
  },
  {
    id: 'ai2',
    productName: 'Cream Jar 30ml',
    originalImage: 'https://images.unsplash.com/photo-1596462502278-27bfdd403348?w=300&h=300&fit=crop',
    generatedImage: 'https://images.unsplash.com/photo-1571781926291-c477ebfd024b?w=300&h=300&fit=crop',
    finishType: 'Frosted Body + Wood-texture Lid',
    status: 'completed',
    quoteRequestId: null,
    createdAt: '2025-05-19T10:30:00Z',
  },
  {
    id: 'ai3',
    productName: 'Pump Bottle 100ml',
    originalImage: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?w=300&h=300&fit=crop',
    generatedImage: '',
    finishType: 'Pearlescent White + Chrome Pump',
    status: 'processing',
    quoteRequestId: null,
    createdAt: '2025-05-24T08:00:00Z',
  },
];

export const mockActiveOrders: ActiveOrder[] = [
  {
    id: 'o1',
    orderNumber: 'O-2025-0156',
    quoteNumber: 'Q-2025-0715',
    productSummary: 'Pump Bottle 100ml — PETG thick-wall, Silk-screen 2-color',
    paymentStatus: 'partial',
    productionStatus: 'in_progress',
    shippingStatus: 'not_shipped',
    totalAmount: 12800.00,
    currency: 'USD',
    estimatedDelivery: '2025-06-18',
  },
];

export const mockPayments: PaymentItem[] = [
  {
    id: 'p1',
    paymentDate: '2025-05-19T09:15:00Z',
    referenceNumber: 'Q-2025-0715',
    amount: 6400.00,
    currency: 'USD',
    status: 'completed',
    methodBrand: 'Mastercard',
    methodLast4: '4242',
  },
  {
    id: 'p2',
    paymentDate: '2025-05-24T10:00:00Z',
    referenceNumber: 'O-2025-0156',
    amount: 4250.00,
    currency: 'USD',
    status: 'pending',
  },
];

export const mockShipments: ShipmentItem[] = [
  {
    id: 's1',
    orderNumber: 'O-2025-0102',
    carrier: 'DHL Express',
    trackingNumber: '1234567890',
    status: 'in_transit',
    estimatedDelivery: '2025-05-28',
  },
];

export const mockMessages: MessageItem[] = [
  {
    id: 'm1',
    title: 'Quote Q-2025-0842 has been updated',
    relatedType: 'quote',
    relatedNumber: 'Q-2025-0842',
    unread: true,
    date: '2025-05-24T09:30:00Z',
    preview: 'Our team has updated the unit pricing based on your requested finish.',
  },
  {
    id: 'm2',
    title: 'Production started for Order O-2025-0156',
    relatedType: 'order',
    relatedNumber: 'O-2025-0156',
    unread: true,
    date: '2025-05-23T14:00:00Z',
    preview: 'Mold preparation is complete. Injection molding begins tomorrow.',
  },
  {
    id: 'm3',
    title: 'Welcome to GBCOSPACK B2B portal',
    relatedType: null,
    relatedNumber: null,
    unread: false,
    date: '2025-05-15T08:00:00Z',
    preview: 'Here are some tips to get the most out of your account.',
  },
];

export const mockSampleRequests: SampleRequest[] = [
  {
    id: 'sp1',
    requestNumber: 'SP-2025-0034',
    productName: 'Airless Bottle 50ml',
    finishSummary: 'Matte soft-touch + UV glossy cap',
    quantity: 5,
    status: 'in_production',
    estimatedCost: 125.00,
    currency: 'USD',
    createdAt: '2025-05-10T11:00:00Z',
  },
];

export const mockUploadedFiles: UploadedFile[] = [
  {
    id: 'f1',
    fileName: 'brand_guideline_v2.pdf',
    fileType: 'application/pdf',
    purpose: 'quote_reference',
    relatedType: 'quote',
    relatedNumber: 'Q-2025-0842',
    uploadedAt: '2025-05-22T13:30:00Z',
  },
  {
    id: 'f2',
    fileName: 'logo_vector_ai.svg',
    fileType: 'image/svg+xml',
    purpose: 'ai_finish_source',
    relatedType: null,
    relatedNumber: null,
    uploadedAt: '2025-05-20T09:00:00Z',
  },
];

export const mockSavedProducts: SavedProduct[] = [
  {
    id: 'prod1',
    productName: 'Airless Bottle 50ml',
    category: 'Bottles',
    sku: 'GB-AIR-50-WH',
    image: 'https://images.unsplash.com/photo-1620916566398-39f1143ab7be?w=400&h=400&fit=crop',
  },
  {
    id: 'prod2',
    productName: 'Cream Jar 30ml Double-wall',
    category: 'Jars',
    sku: 'GB-JAR-30-DW',
    image: 'https://images.unsplash.com/photo-1596462502278-27bfdd403348?w=400&h=400&fit=crop',
  },
  {
    id: 'prod3',
    productName: 'Pump Bottle 100ml PETG',
    category: 'Bottles',
    sku: 'GB-PMP-100-PG',
    image: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?w=400&h=400&fit=crop',
  },
];

export const mockCompanyProfile: CompanyProfile = {
  completionPercent: 60,
  missingFields: ['shipping_address', 'tax_id', 'contact_phone'],
  companyName: 'Lumiere Beauty Co.',
};

export const mockMarketSettings: MarketSetting = {
  country: 'United States',
  language: 'English',
  displayCurrency: 'USD',
  quoteBaseCurrency: 'USD',
  exchangeRateDate: '2025-05-24',
};